import React from 'react';
import { Link } from 'react-router-dom';
import { Server, CheckCircle, ArrowRight, Cloud, Database, Network, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import SEO from '../components/SEO';

const InfrastructureSolutions = () => {
  const components = [
    {
      icon: Cloud,
      title: 'Cloud Computing',
      description: 'Comprehensive cloud infrastructure setup and management across Cloud Service Providers'
    },
    {
      icon: Database,
      title: 'Storage Solutions',
      description: 'Scalable and secure storage architectures for enterprise data management'
    },
    {
      icon: Network,
      title: 'Networking & Virtualization',
      description: 'Advanced networking solutions with virtualization for optimal performance'
    },
    {
      icon: Shield,
      title: 'Security Infrastructure',
      description: 'Built-in security components and compliance standards for protection'
    }
  ];

  const benefits = [
    'End-to-end cloud-native infrastructure',
    'Multi-cloud support',
    'Cost optimization strategies',
    'Performance tuning and monitoring',
    'Scalable architecture design',
    ' 24/7 infrastructure support'
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Cloud Infrastructure Solutions — Cloud Native Architecture | NIVA Digital AI"
        description="End-to-end cloud native infrastructure solutions. Expert architecture design, migration services, and optimization for Cloud Service Providers."
        keywords="cloud infrastructure, cloud native architecture, cloud migration, cloud solutions, cloud infrastructure services"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/infrastructure`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-sky-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Server size={20} />
                <span>Infrastructure Solutions</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Cloud-Native
                <span className="block text-red-600 mt-2">Infrastructure</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Start your cloud journey and optimize both performance and costs with end-to-end cloud-native infrastructure solutions.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Get Started <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-sky-50 rounded-2xl p-8 shadow-2xl">
                <Server className="text-blue-600 mx-auto" size={200} strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Components */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Infrastructure Components
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive infrastructure services covering all aspects of cloud computing
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {components.map((component, index) => {
              const IconComponent = component.icon;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl">
                  <CardHeader>
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-red-600" size={28} />
                    </div>
                    <CardTitle className="text-2xl mb-2">{component.title}</CardTitle>
                    <CardDescription className="text-base">{component.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Deliver
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Focus */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Security-First Approach
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                94% of businesses experienced improved security after migrating to the cloud with our solutions.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Best Practice Implementation</h3>
                    <p className="text-gray-600">Industry-standard security measures and compliance protocols</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">HIPAA Compliance</h3>
                    <p className="text-gray-600">Specialized expertise in healthcare infrastructure compliance</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Continuous Monitoring</h3>
                    <p className="text-gray-600">24/7 security monitoring and threat detection</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-sky-50 rounded-2xl p-12 shadow-2xl">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow">
                    <Shield className="text-blue-600 mb-2" size={32} />
                    <h4 className="font-semibold">Enterprise Security</h4>
                    <p className="text-sm text-gray-600 mt-1">Advanced protection measures</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <CheckCircle className="text-blue-600 mb-2" size={32} />
                    <h4 className="font-semibold">Compliance Ready</h4>
                    <p className="text-sm text-gray-600 mt-1">Meet regulatory requirements</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Modernize Your Infrastructure?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get a free infrastructure assessment and consultation
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Us <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InfrastructureSolutions;